import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Clock, CalendarCheck, Phone } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Chatbot from "@/components/Chatbot";
import ShareButtons from "@/components/ShareButtons";
import useSeo from "@/hooks/useSeo";
import { CALENDLY_URL, PHONE, PHONE_TEL } from "@/lib/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`${API}/articles/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(setArticle)
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (article?.title) document.title = `${article.title} — MD Courtage`;
  }, [article]);

  const canonicalUrl = article ? `https://mdcourtage.fr/article/${article.slug}` : "";
  const articleJsonLd = article
    ? [
        {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.excerpt,
          image: article.image,
          author: { "@type": "Person", name: "Maël", url: "https://mdcourtage.fr" },
          publisher: {
            "@type": "Organization",
            name: "MD Courtage",
            logo: {
              "@type": "ImageObject",
              url: "https://customer-assets.emergentagent.com/job_placement-credit/artifacts/rwikb7go_Adora%20Montminy%20%284000%20x%204000%20px%29%20%281%29.png",
            },
          },
          datePublished: article.published_at || article.created_at,
          dateModified: article.updated_at || article.published_at,
          mainEntityOfPage: canonicalUrl,
          articleSection: article.category,
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://mdcourtage.fr/" },
            { "@type": "ListItem", position: 2, name: "Conseils", item: "https://mdcourtage.fr/#blog" },
            { "@type": "ListItem", position: 3, name: article.title, item: canonicalUrl },
          ],
        },
      ]
    : null;

  useSeo({
    title: article ? `${article.title} — MD Courtage` : undefined,
    description: article?.excerpt,
    url: canonicalUrl,
    image: article?.image,
    type: "article",
    jsonLd: articleJsonLd,
  });

  if (notFound) {
    return (
      <div className="bg-md-bg min-h-screen">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 pt-40 pb-32 text-center">
          <h1 className="font-serif text-4xl text-md-primary font-medium mb-4">Article introuvable</h1>
          <p className="text-md-muted mb-8">Cet article n'existe pas (ou plus). Découvrez les autres conseils.</p>
          <Link to="/#blog" className="inline-flex items-center gap-2 bg-md-secondary hover:bg-md-secondaryHover text-white px-6 py-3 rounded-full font-semibold">
            <ArrowLeft className="w-4 h-4" /> Retour aux conseils
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return <div className="min-h-screen grid place-items-center text-md-muted">Chargement…</div>;
  }

  return (
    <div className="bg-md-bg min-h-screen">
      <Navbar />
      <article className="pt-32 md:pt-40 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/#blog" className="inline-flex items-center gap-2 text-sm text-md-muted hover:text-md-primary mb-8">
            <ArrowLeft className="w-4 h-4" /> Tous les conseils
          </Link>

          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-md-secondary mb-4">{article.category}</div>
          <h1 className="font-serif text-4xl md:text-5xl text-md-primary font-medium leading-tight mb-6" data-testid="article-title">
            {article.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-md-muted mb-10">
            <span>{article.date}</span>
            <span>•</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" /> {article.read_time}</span>
          </div>

          {article.image && (
            <img src={article.image} alt={article.title} className="w-full h-80 object-cover rounded-3xl mb-10" />
          )}

          <p className="font-serif italic text-xl text-md-primary/80 leading-relaxed mb-10" data-testid="article-excerpt">
            {article.excerpt}
          </p>

          <div className="prose-md text-md-text leading-relaxed" data-testid="article-content">
            <ReactMarkdown>{article.content}</ReactMarkdown>
          </div>

          <ShareButtons title={article.title} excerpt={article.excerpt} />

          {/* CTA */}
          <div className="mt-16 bg-md-primary text-white rounded-3xl p-8 md:p-10 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-60 h-60 bg-md-secondary/20 rounded-full blur-3xl" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.25em] text-md-secondary font-bold mb-3">On en discute ?</div>
              <h3 className="font-serif text-3xl md:text-4xl mb-4 leading-tight">Votre situation est unique. <span className="italic">Mon conseil aussi.</span></h3>
              <p className="text-white/80 mb-7 max-w-xl">Cet article vous a interpellé ? Échangeons 20 minutes pour identifier les leviers adaptés à votre profil — gratuit et sans engagement.</p>
              <div className="flex flex-wrap gap-3">
                <a href={CALENDLY_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-md-secondary hover:bg-md-secondaryHover text-white px-6 py-3 rounded-full font-semibold">
                  <CalendarCheck className="w-4 h-4" /> Prendre rendez-vous
                </a>
                <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-2 border border-white/30 text-white px-6 py-3 rounded-full font-semibold hover:bg-white/10">
                  <Phone className="w-4 h-4" /> {PHONE}
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>
      <Footer />
      <Chatbot />
    </div>
  );
}
